import { StoredFile, type UploadKind } from '@deizi/shared';
import * as tus from 'tus-js-client';
import { api, csrfHeaders } from './api';

export type UploadStatus = 'uploading' | 'paused' | 'offline' | 'done' | 'error';

export interface UploadHandle {
  pause: () => void;
  resume: () => void;
  abort: () => void;
  /** The stored file once the upload is complete. */
  done: Promise<StoredFile>;
}

const CHUNK = 8 * 1024 * 1024;

/**
 * Resumable upload (tus) of one file, then `POST /uploads/{id}/complete`. Retries on its own after
 * connection drops ("Sem sinal…") and resumes from the last chunk, also after a page reload
 * (fingerprint kept in localStorage by tus-js-client).
 */
export function startUpload(
  file: File,
  kind: UploadKind,
  on: {
    progress?: (sent: number, total: number) => void;
    status?: (s: UploadStatus, error?: string) => void;
  } = {},
): UploadHandle {
  let resolve!: (f: StoredFile) => void;
  let reject!: (e: Error) => void;
  const done = new Promise<StoredFile>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  // Callers that never await the promise must not see unhandled rejections.
  done.catch(() => undefined);

  const upload = new tus.Upload(file, {
    endpoint: '/api/v1/uploads',
    chunkSize: CHUNK,
    retryDelays: [0, 1000, 3000, 5000, 10_000, 20_000, 30_000, 60_000],
    removeFingerprintOnSuccess: true,
    metadata: { filename: file.name, kind },
    headers: csrfHeaders(),
    onProgress: (sent, total) => on.progress?.(sent, total),
    onShouldRetry: (err) => {
      const status = err.originalResponse?.getStatus() ?? 0;
      if (status === 0) on.status?.('offline');
      // Client errors (auth, type, size) will not get better by retrying.
      return status === 0 || status >= 500 || status === 409 || status === 423;
    },
    onError: (err) => {
      const res = err instanceof tus.DetailedError ? err.originalResponse : null;
      const status = res?.getStatus() ?? 0;
      const body = res?.getBody()?.trim();
      const msg =
        status === 413
          ? 'Arquivo grande demais para este tipo.'
          : status === 415
            ? 'Tipo de arquivo não aceito aqui.'
            : body || 'Falha no envio.';
      on.status?.('error', msg);
      reject(new Error(msg));
    },
    onSuccess: () => {
      const id = (upload.url ?? '').split('/').pop() ?? '';
      api
        .post(`/uploads/${id}/complete`, StoredFile)
        .then((stored) => {
          on.status?.('done');
          resolve(stored);
        })
        .catch((err: unknown) => {
          const e = err instanceof Error ? err : new Error(String(err));
          on.status?.('error', e.message);
          reject(e);
        });
    },
  });

  const begin = () => {
    on.status?.('uploading');
    void upload.findPreviousUploads().then((previous) => {
      const last = previous[0];
      if (last) upload.resumeFromPreviousUpload(last);
      upload.start();
    });
  };
  begin();
  return {
    pause: () => {
      void upload.abort();
      on.status?.('paused');
    },
    resume: () => {
      on.status?.('uploading');
      upload.start();
    },
    abort: () => {
      void upload.abort(true);
      reject(new Error('cancelado'));
    },
    done,
  };
}

const mb = (n: number) => (n / 1024 / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 1 });

/** "41% · 0,9 de 2,2 MB" */
export function progressLabel(sent: number, total: number): string {
  const pct = total ? Math.floor((sent / total) * 100) : 0;
  return `${pct}% · ${mb(sent)} de ${mb(total)} MB`;
}
